import {useEffect, useRef, useState} from 'react';
import {Terminal as XTerm} from '@xterm/xterm';
import {FitAddon} from '@xterm/addon-fit';
import {WebLinksAddon} from '@xterm/addon-web-links';
import {io} from 'socket.io-client';
import { backButton } from '@tma.js/sdk-react';
import '@xterm/xterm/css/xterm.css';
import {useNavigate} from "react-router-dom";
import { useAuthContext } from '../../../app/providers/AuthProvider';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faKeyboard,
    faChevronUp,
    faChevronDown,
    faArrowUp,
    faArrowDown,
    faBars,
    faTimes
} from '@fortawesome/free-solid-svg-icons';

const WS_URL = import.meta.env.VITE_WS_URL || 'http://localhost:3000';

const QUICK_COMMANDS = [
    { label: 'ls', command: 'ls -la\r', desc: 'Список файлов' },
    { label: 'clear', command: 'clear\r', desc: 'Очистить' },
    { label: 'top', command: 'top\r', desc: 'Процессы' },
    { label: 'cd ..', command: 'cd ..\r', desc: 'Назад' },
    { label: 'pwd', command: 'pwd\r', desc: 'Где я?' },
    { label: 'exit', command: 'exit\r', desc: 'Выход' },
];

const SPECIAL_KEYS = [
    { label: 'Tab', code: '\t' },
    { label: 'Ctrl+C', code: '\x03' },
    { label: 'Ctrl+D', code: '\x04' },
    { label: 'Esc', code: '\x1b' },
];

function Terminal() {
    const { authToken, initDataRaw } = useAuthContext();
    const terminalRef = useRef(null);
    const xtermRef = useRef(null);
    const socketRef = useRef(null);
    const [connected, setConnected] = useState(false);
    const [showQuickCommands, setShowQuickCommands] = useState(true);
    const [keyboardVisible, setKeyboardVisible] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        if (!terminalRef.current) return;

        // Detect mobile device for better font size
        const isMobile = window.innerWidth < 768;

        // Initialize xterm.js
        const term = new XTerm({
            cursorBlink: true,
            fontSize: isMobile ? 12 : 14,
            fontFamily: 'Menlo, Monaco, "Courier New", monospace',
            theme: {
                background: '#1e1e1e',
                foreground: '#ffffff',
            },
            rows: isMobile ? 30 : 50,
            cols: isMobile ? 40 : 80,
            scrollback: 1000,
            // Enable smooth scrolling on mobile
            smoothScrollDuration: 100
        });

        const fitAddon = new FitAddon();
        term.loadAddon(fitAddon);
        term.loadAddon(new WebLinksAddon());

        term.open(terminalRef.current);

        // Wait for terminal to be ready before fitting
        setTimeout(() => {
            fitAddon.fit();
        }, 0);

        xtermRef.current = term;

        // Connect to Socket.io with auth
        const authConfig = authToken
            ? {token: authToken}  // Browser access with token
            : {initData: initDataRaw};  // Telegram WebApp access

        const socket = io(WS_URL, {
            auth: authConfig
        });
        socketRef.current = socket;

        socket.on('connect', () => {
            console.log('Connected to server');
            setConnected(true);
            socket.emit('terminal:create');
        });

        socket.on('disconnect', () => {
            console.log('Disconnected from server');
            setConnected(false);
        });

        socket.on('connect_error', (error) => {
            console.error('Connection error:', error.message);
            if (error.message === 'Authentication required' || error.message === 'Access denied') {
                term.write('\r\n\x1b[1;31mError: ' + error.message + '\x1b[0m\r\n');
            }
        });

        socket.on('terminal:data', (data) => {
            term.write(data);
        });

        // Send input to server
        term.onData((data) => {
            socket.emit('terminal:input', data);
        });

        // Handle resize
        const handleResize = () => {
            if (term._core && term._core._renderService) {
                fitAddon.fit();
                socket.emit('terminal:resize', {
                    cols: term.cols,
                    rows: term.rows
                });
            }
        };

        window.addEventListener('resize', handleResize);

        // Cleanup
        return () => {
            window.removeEventListener('resize', handleResize);
            socket.disconnect();
            term.dispose();
        };
    }, [authToken, initDataRaw]);

    useEffect(() => {
        try {
            // Show Telegram BackButton when in Telegram WebApp
            backButton.show?.();

            const handleBack = () => {
                // Go back to home page
                navigate("/");
            };

            // Listen for Telegram back button clicks
            backButton.on?.('click', handleBack);

            // Cleanup on unmount
            return () => {
                backButton.off?.('click', handleBack);
                backButton.hide?.();
            };
        } catch (err) {
            console.warn('Failed to setup back button:', err);
        }
    }, [navigate]);

    const executeQuickCommand = (command) => {
        if (socketRef.current && xtermRef.current) {
            socketRef.current.emit('terminal:input', command);
        }
    };

    const toggleKeyboard = () => {
        if (xtermRef.current) {
            if (keyboardVisible) {
                // Blur terminal to hide keyboard
                xtermRef.current.blur();
            } else {
                // Focus terminal to show keyboard
                xtermRef.current.focus();
            }
            setKeyboardVisible(!keyboardVisible);
        }
    };

    const sendArrowKey = (direction) => {
        if (socketRef.current) {
            // ANSI escape codes for arrow keys
            const arrowCodes = {
                up: '\x1b[A',      // Arrow Up
                down: '\x1b[B',    // Arrow Down
            };
            socketRef.current.emit('terminal:input', arrowCodes[direction]);
        }
    };

    return (
        <div className="relative h-screen flex flex-col bg-black">
            {/* Terminal Container */}
            <div className="flex-1 overflow-hidden relative">
                <div ref={terminalRef} className="h-full w-full"/>

                {/* Floating History Navigation - Right Side */}
                <div className="fixed right-2 top-1/2 -translate-y-1/2 flex flex-col gap-2 z-20">
                    <button
                        onClick={() => sendArrowKey('up')}
                        className="w-12 h-12 rounded-full bg-theme-button-color text-theme-button-text-color shadow-lg hover:opacity-80 transition-all active:scale-95 flex items-center justify-center"
                        title="История назад (↑)"
                    >
                        <FontAwesomeIcon icon={faArrowUp} size="lg" />
                    </button>
                    <button
                        onClick={() => sendArrowKey('down')}
                        className="w-12 h-12 rounded-full bg-theme-button-color text-theme-button-text-color shadow-lg hover:opacity-80 transition-all active:scale-95 flex items-center justify-center"
                        title="История вперед (↓)"
                    >
                        <FontAwesomeIcon icon={faArrowDown} size="lg" />
                    </button>
                </div>
            </div>

            {/* Bottom Control Panel - Compact */}
            <div className="bg-theme-section-bg-color border-t border-theme-section-separator-color">
                {/* Minimized - Single Row */}
                {!showQuickCommands && (
                    <div className="flex items-center gap-2 p-2">
                        <button
                            onClick={() => setShowQuickCommands(true)}
                            className="px-4 py-2 rounded-lg bg-theme-button-color text-theme-button-text-color hover:opacity-80 transition-opacity flex items-center gap-2"
                        >
                            <FontAwesomeIcon icon={faBars} />
                            <span className="text-sm">Команды</span>
                        </button>
                        <button
                            onClick={toggleKeyboard}
                            className="flex-1 py-2 rounded-lg bg-theme-hint-color/50 text-theme-text-color hover:opacity-80 transition-opacity flex items-center justify-center gap-2"
                        >
                            <FontAwesomeIcon icon={faKeyboard} />
                            <span className="text-sm">{keyboardVisible ? 'Скрыть клаву' : 'Показать клаву'}</span>
                        </button>
                    </div>
                )}

                {/* Expanded - Full Panel */}
                {showQuickCommands && (
                    <div className="p-3 space-y-3">
                        {/* Header with Close */}
                        <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-theme-text-color">Быстрые команды</span>
                            <button
                                onClick={() => setShowQuickCommands(false)}
                                className="w-8 h-8 rounded-full bg-theme-hint-color/30 text-theme-text-color hover:opacity-80 transition-opacity flex items-center justify-center"
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>

                        {/* Commands Grid - 2 columns */}
                        <div className="grid grid-cols-2 gap-2">
                            {QUICK_COMMANDS.map((cmd, index) => (
                                <button
                                    key={index}
                                    onClick={() => executeQuickCommand(cmd.command)}
                                    className="p-3 rounded-lg bg-theme-button-color text-theme-button-text-color hover:opacity-80 transition-opacity text-left"
                                >
                                    <div className="font-medium text-sm">{cmd.label}</div>
                                    <div className="text-xs opacity-70 mt-0.5">{cmd.desc}</div>
                                </button>
                            ))}
                        </div>

                        {/* Special Keys Row */}
                        <div className="flex gap-2 pt-2 border-t border-theme-section-separator-color">
                            {SPECIAL_KEYS.map((key, index) => (
                                <button
                                    key={index}
                                    onClick={() => executeQuickCommand(key.code)}
                                    className="flex-1 py-2 text-xs rounded-lg bg-theme-hint-color/30 text-theme-text-color hover:opacity-80 transition-opacity"
                                >
                                    {key.label}
                                </button>
                            ))}
                        </div>

                        {/* Keyboard Toggle */}
                        <button
                            onClick={toggleKeyboard}
                            className="w-full py-2.5 rounded-lg bg-theme-hint-color/50 text-theme-text-color hover:opacity-80 transition-opacity flex items-center justify-center gap-2"
                        >
                            <FontAwesomeIcon icon={faKeyboard} />
                            <span className="text-sm">{keyboardVisible ? 'Скрыть клавиатуру' : 'Показать клавиатуру'}</span>
                        </button>
                    </div>
                )}
            </div>

            {/* Connection Status - Top Left, Compact */}
            <div className="fixed top-2 left-2 backdrop-blur-md bg-black/50 rounded-full px-3 py-1.5 shadow-lg z-10">
                <div className="flex items-center gap-1.5">
                    <span className={`inline-block w-1.5 h-1.5 rounded-full ${connected ? 'bg-green-500' : 'bg-red-500'}`}></span>
                    <span className="text-white text-xs font-medium">
                        {connected ? 'ON' : 'OFF'}
                    </span>
                </div>
            </div>
        </div>
    );
}

export default Terminal;
